import {useEffect, useRef, useState} from 'react'

import {CHAR_SPEED, POST_REDACT_WAIT, POST_TYPE_WAIT, REDACT_DURATION} from '../constants'
import {drawRedaction} from '../utils'

export const LoadingLine = ({
  text,
  keyword,
  startDelay,
  onDone,
}: {
  text: string
  keyword: string
  startDelay: number
  onDone: () => void
}) => {
  const [typedChars, setTypedChars] = useState(0)
  const [showRedaction, setShowRedaction] = useState(false)
  const [started, setStarted] = useState(false)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const keywordRef = useRef<HTMLSpanElement>(null)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  // Start typing after delay
  useEffect(() => {
    const t = setTimeout(() => setStarted(true), startDelay)
    return () => clearTimeout(t)
  }, [startDelay])

  // Typewriter + redaction sequence
  useEffect(() => {
    if (!started) {
      return
    }

    const timers: ReturnType<typeof setTimeout>[] = []

    for (let i = 1; i <= text.length; i++) {
      timers.push(setTimeout(() => setTypedChars(i), i * CHAR_SPEED))
    }

    const typingEnd = text.length * CHAR_SPEED
    timers.push(setTimeout(() => setShowRedaction(true), typingEnd + POST_TYPE_WAIT))
    timers.push(setTimeout(() => onDoneRef.current(), typingEnd + POST_TYPE_WAIT + REDACT_DURATION + POST_REDACT_WAIT))

    return () => timers.forEach(clearTimeout)
  }, [started, text])

  // Trigger canvas redaction animation, positioned over the keyword span
  useEffect(() => {
    if (!showRedaction || !canvasRef.current || !keywordRef.current) {
      return
    }

    const span = keywordRef.current
    const canvas = canvasRef.current
    canvas.width = span.offsetWidth + 8
    canvas.height = span.offsetHeight
    canvas.style.left = `${span.offsetLeft - 4}px`
    drawRedaction(canvas)
  }, [showRedaction])

  const keywordStart = text.indexOf(keyword)
  const beforeKeyword = text.slice(0, keywordStart)
  const afterKeyword = text.slice(keywordStart + keyword.length)
  const typedLen = typedChars

  return (
    <p className="font-typewriter relative h-[28px] text-[19px] leading-[28px] text-moss-white/75">
      {started && typedLen > 0 && (
        <>
          {typedLen <= keywordStart && text.slice(0, typedLen)}
          {typedLen > keywordStart && (
            <>
              {beforeKeyword}
              <span ref={keywordRef}>{keyword.slice(0, Math.min(typedLen - keywordStart, keyword.length))}</span>
              {typedLen > keywordStart + keyword.length &&
                afterKeyword.slice(0, typedLen - keywordStart - keyword.length)}
            </>
          )}
        </>
      )}
      {started && typedLen > 0 && !showRedaction && (
        <span className="text-green-neon" style={{animation: 'blink 1s step-end infinite'}}>
          |
        </span>
      )}
      <canvas
        ref={canvasRef}
        height={28}
        className="pointer-events-none absolute top-0"
        style={{display: showRedaction ? 'block' : 'none'}}
      />
    </p>
  )
}
